import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';

interface FormLabelProps {
  children: React.ReactNode;
  className?: string;
  /** Marks the field with a red asterisk. */
  required?: boolean;
  /** Marks the field as one the visitor may leave blank. */
  optional?: boolean;
  htmlFor?: string;
}

/**
 * A field label that says whether the field has to be filled in.
 *
 * Every collected field is one or the other, and saying so up front is cheaper for the
 * visitor than discovering it when the form refuses to go on. Required carries a red *,
 * optional carries the word — an unmarked label means the field is not editable at all.
 */
export const FormLabel: React.FC<FormLabelProps> = ({ children, className, required, optional, htmlFor }) => {
  const { t } = useTranslation();
  return (
    <label className={className} htmlFor={htmlFor}>
      {children}
      {required && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
      {optional && !required && (
        <span className="ml-1.5 normal-case tracking-normal font-medium text-black/25 dark:text-white/25">
          ({t.form.optional})
        </span>
      )}
    </label>
  );
};

/** The red line under a field that failed validation. Renders nothing when there is no error. */
export const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="text-[9px] text-red-500 mt-1.5 font-medium">{message}</p> : null;

/** The one-line legend that explains the asterisks, shown once per form. */
export const RequiredNote: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  return (
    <p className={className ?? 'text-[9px] text-black/30 dark:text-white/30'}>
      <span className="text-red-500">*</span> — {t.form.requiredNote}
    </p>
  );
};
